import axios, { AxiosInstance } from 'axios';
import {
  ConfigApi,
  createApiRef,
  DiscoveryApi,
  IdentityApi,
  OAuthApi,
} from '@backstage/core-plugin-api';
import { getCookie } from '../components/utils/Utils';
import { getLocalToken, tokenPromptService } from '../utils/localDevUtils';

export interface AxiosInstanceProviderApi {
  getInstance(): Promise<AxiosInstance>;
}

export const axiosInstanceProviderApiRef =
  createApiRef<AxiosInstanceProviderApi>({
    id: 'cbre.devx.axios.instance',
  });

export class AxiosInstanceProviderApiImpl implements AxiosInstanceProviderApi {
  private axiosInstance: AxiosInstance | undefined = undefined;
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;
  private readonly microsoftAuthApi: OAuthApi;
  private readonly configApi: ConfigApi;

  constructor(
    discoveryApi: DiscoveryApi,
    identityApi: IdentityApi,
    microsoftAuthApi: OAuthApi,
    configApi: ConfigApi,
  ) {
    this.discoveryApi = discoveryApi;
    this.identityApi = identityApi;
    this.microsoftAuthApi = microsoftAuthApi;
    this.configApi = configApi;
  }

  // Lazy initialization
  async init() {
    if (this.axiosInstance) {
      return this.axiosInstance;
    }

    // goServicesProxy: same-origin URL so the dev-server proxy forwards Go calls and avoids CORS.
    const API_BASE_URL = this.configApi.getOptionalBoolean(
      'app.goServicesProxy',
    )
      ? `${window.location.origin}/api/`
      : await this.discoveryApi.getBaseUrl('');

    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
    });
    this.axiosInstance.interceptors.request.use(async (config: any) => {
      config.timeout = 30000;
      if (
        `${config.url}`.includes('discovery/') ||
        `${config.url}`.includes('service/')
      ) {
        const token =
          getLocalToken('devx_ms_token') ||
          getLocalToken('msToken') ||
          (await this.microsoftAuthApi.getAccessToken());
        config.headers = {
          Authorization: `Bearer ${token}`,
          'api-version': '1.0',
          'x-token': 'devx',
          ...config.headers,
        };
        if (`${config.url}`.includes('chat/stream')) {
          config.responseType = 'stream';
          config.headers = {
            Authorization: `Bearer ${token}`,
            'api-version': '1.0',
            'x-token': 'devx',
            ...config.headers,
          };
          config.timeout = 300000;
        }
      } else {
        config.baseURL = await this.discoveryApi.getBaseUrl('');
        const backstageIdToken =
          getCookie('token') || (await this.identityApi.getCredentials()).token;
        config.headers = {
          Authorization: `Bearer ${backstageIdToken}`,
          'api-version': '1.0',
          ...config.headers,
        };
      }
      return config;
    });

    this.axiosInstance.interceptors.response.use(
      response => {
        //TODO handle response block by WAF based of resposne header
        if (
          response.headers &&
          response.headers['x-xss-protection'] === '1; mode=block'
        ) {
          //ignore all get calls from backend
          if (response.config?.method === 'get') {
            return response;
          }

          // Ignore SharePoint upload endpoint - response contains safe file URLs
          if (`${response.config?.url}`.includes('sharepoint-upload')) {
            console.log(
              '[XSS Protection] Bypassed for SharePoint upload endpoint',
            );
            return response;
          }

          throw new Error(
            "We're sorry for the inconvenience. Our system has identified cross-site scripting in your code snippet, which is preventing us from processing your request. Please remove them and try again.",
          );
        }
        return response;
      },
      async error => {
        const originalConfig = error.config;
        if (error?.response?.status == 401 && !originalConfig._retry) {
          const isFrontendOnly =
            this.configApi.getOptionalBoolean('app.frontendOnly') ?? false;

          // In frontendOnly mode, prompt the developer to enter fresh tokens
          // via a dialog instead of silently retrying with stale/placeholder values.
          if (isFrontendOnly) {
            await tokenPromptService.prompt();
            window.location.reload();
            return;
          }

          if (
            `${originalConfig.url}`.includes('discovery/') ||
            `${originalConfig.url}`.includes('service/')
          ) {
            const token =
              getLocalToken('devx_ms_token') ||
              getLocalToken('msToken') ||
              (await this.microsoftAuthApi.getAccessToken());
            originalConfig.headers = {
              'api-version': '1.0',
              ...originalConfig.headers,
              Authorization: `Bearer ${token}`,
            };
          } else {
            const backstageIdToken = (await this.identityApi.getCredentials())
              .token;
            originalConfig.headers = {
              'api-version': '1.0',
              ...originalConfig.headers,
              Authorization: `Bearer ${backstageIdToken}`,
            };
          }

          originalConfig._retry = true;
          return this.axiosInstance?.request(originalConfig);
        }
        return Promise.reject(error);
      },
    );

    return this.axiosInstance;
  }

  getInstance() {
    return this.init();
  }
}
